/* eslint-disable prettier/prettier */
'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

import { cn } from 'utils/shadcn';
import { Button } from 'app/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'app/components/ui/command1';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'app/components/ui/popover';
import { useFormContext } from 'react-hook-form';

import { useProductsSlice } from 'app/pages/Product/slice';
import { useCourseSlice } from '../slice';

export default function Tags() {
  const { useLazyGetCourseTagsQuery } = useCourseSlice();
  const [getTags, { data: tags, error, isLoading }] =
    useLazyGetCourseTagsQuery();

  const form = useFormContext();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Fetch tags on search query change
  React.useEffect(() => {
    const fetchTags = async () => {
      if (searchQuery.trim() === '') {
        await getTags({});
      } else {
        await getTags({ search: searchQuery });
      }
    };
    fetchTags();
  }, [searchQuery]);

  // Handle adding a tag
  const handleAddTag = tag => {
    const currentTags = form.watch('tags') || [];
    const existingTag = currentTags.find(tags => tags?.tag_id === tag.tag_id);

    if (!existingTag) {
      const updatedTags = [...currentTags, tag];
      form.setValue('tags', updatedTags);
    }
  };

  // Handle adding a new tag from search query
  const handleAddNewTag = () => {
    if (searchQuery === '') return;

    const newTags = form.getValues('newTags') || [];
    const updatedNewTags = [...newTags, searchQuery];
    form.setValue('newTags', updatedNewTags);
    setSearchQuery('');
    setOpen(false);
  };

  // Handle removing a tag
  const handleRemoveTag = (id: number) => {
    const tags = form.watch('tags') || [];
    const updatedTags = tags.filter(t => t.tag_id !== id);
    form.setValue('tags', updatedTags);
  };

  // Handle removing a new tag
  const handleRemoveNewTag = (tag: string) => {
    const newTags = form.getValues('newTags') || [];
    const updatedNewTags = newTags.filter(t => t !== tag);
    form.setValue('newTags', updatedNewTags);
  };

  return (
    <>
      <div className="tags">
        <div className="font-medium text-[13px] mb-[6px]">Tags</div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto justify-between py-2"
            >
              <div className="flex gap-2 justify-start text-sm">
                Select Tags
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput
                placeholder="Search or Add Tags"
                onValueChange={val => setSearchQuery(val)}
                value={searchQuery}
              />
              <CommandEmpty>
                <div>No Tag found.</div>
                {searchQuery !== '' && (
                  <Button className="mt-2" onClick={handleAddNewTag}>
                    <Plus className="ml-1 w-4 h-4" />
                    Add
                  </Button>
                )}
              </CommandEmpty>

              <CommandGroup>
                <CommandList>
                  {tags?.response?.data?.map(tag => {
                    const selectedTags = form.watch('tags') || [];
                    const isTagSelected = selectedTags.some(
                      t => t.tag_id === tag?.tag_id,
                    );

                    return (
                      <CommandItem
                        key={tag.tag_id}
                        value={tag.tag_name}
                        onSelect={() => handleAddTag(tag)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            isTagSelected ? 'opacity-100' : 'opacity-0',
                          )}
                        />

                        {tag.tag_name}
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="flex flex-wrap gap-2 justify-start mt-3">
          {form.watch('tags')?.map(tag => {
            return (
              <div
                key={tag}
                className="px-2 py-1 rounded-xl border bg-slate-200 text-xs font-medium"
              >
                {tag.tag_name || 'New Tag'}{' '}
                <span
                  className="ml-1 cursor-pointer"
                  onClick={() => handleRemoveTag(tag.tag_id)}
                >
                  x
                </span>
              </div>
            );
          })}

          {form.watch('newTags')?.map((tag, i) => (
            <div
              key={i}
              className="px-2 py-1 rounded-xl border bg-slate-200 text-xs font-medium"
            >
              {tag}{' '}
              <span
                className="ml-1 cursor-pointer"
                onClick={() => handleRemoveNewTag(tag)}
              >
                x
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
